import type { AdapterOHLCVRequest, AdapterOptions, ExchangeAdapter } from './types';
import { ReconnectingSocket } from './ws';
import { ProviderError } from '../types';
import type { StreamStatus, Unsubscribe } from '../types';
import type { TradingPair } from '../../types/pair';
import type { OHLCV, Timeframe } from '../../types/ohlcv';
import type { Ticker24h } from '../../types/ticker';
import { aggregateToTimeframe, bucketStart } from '../../utils/aggregate';
import { mergeCandles } from '../../utils/candles';
import { timeframeToMs } from '../../utils/timeframe';

/**
 * Binance spot public market data (no API key, CORS `*`). Endpoint facts this
 * adapter relies on, verified live 2026-10-02:
 *   - GET /api/v3/klines?symbol&interval&startTime&endTime&limit returns
 *     `[openTime, open, high, low, close, volume, closeTime, …]` rows (prices
 *     as strings), oldest first, with startTime <= openTime <= endTime;
 *     `limit` is capped at 1000.
 *   - `3d` klines are phased from each symbol's listing day (ETHBTC, BNBUSDT
 *     and SOLUSDT open on three different days), so 3D is rolled up from 1d
 *     into the epoch-aligned buckets of `bucketStart`.
 *   - api.binance.com answers HTTP 451 to US visitors; data-api.binance.vision
 *     serves the same public market data.
 *   - exchangeInfo?permissions=SPOT&symbolStatus=TRADING&showPermissionSets=false
 *     lists only trading spot symbols (2.5 MB instead of 17.7 MB); every
 *     symbol is baseAsset + quoteAsset.
 *   - ticker/24hr?type=MINI&symbolStatus=TRADING has open/high/low/last and
 *     base/quote volume per symbol, but no percent change and no bid/ask.
 *   - wss://stream.binance.com:9443/stream (fallback data-stream.binance.vision)
 *     takes a SUBSCRIBE request of at most 1024 streams per connection and
 *     pushes `{ stream, data }` frames. Kline streams stay silent while
 *     nothing trades.
 */
const REST_BASES = ['https://api.binance.com/api/v3', 'https://data-api.binance.vision/api/v3'];
const STREAM_URLS = ['wss://stream.binance.com:9443/stream', 'wss://data-stream.binance.vision/stream'];
const MAX_KLINES = 1000;
const MAX_STREAMS = 1024;
const PAIRS_TTL_MS = 10 * 60_000;
// Quiet markets push nothing; a request the server answers keeps the socket from being recycled as stale.
const HEARTBEAT = { intervalMs: 30_000, message: JSON.stringify({ method: 'LIST_SUBSCRIPTIONS', id: 2 }) };

const INTERVAL: Partial<Record<Timeframe, string>> = {
  '1m': '1m',
  '3m': '3m',
  '5m': '5m',
  '15m': '15m',
  '30m': '30m',
  '1h': '1h',
  '2h': '2h',
  '4h': '4h',
  '6h': '6h',
  '12h': '12h',
  '1D': '1d',
  '1W': '1w',
  '1M': '1M',
};

/** Timeframes rolled up from a finer native one (fixed-length timeframes only). */
const DERIVED: Partial<Record<Timeframe, Timeframe>> = {
  '3D': '1D',
};

export function toBinanceSymbol(pair: string): string {
  return pair.trim().toUpperCase().replace('/', '');
}

export function mapBinanceKlines(rows: unknown): OHLCV[] {
  if (!Array.isArray(rows)) throw new ProviderError('Unrecognized Binance kline response', undefined, rows);
  const out: OHLCV[] = [];
  for (const row of rows) {
    if (!Array.isArray(row) || row.length < 6) continue;
    out.push({
      timestamp: Number(row[0]),
      open: Number(row[1]),
      high: Number(row[2]),
      low: Number(row[3]),
      close: Number(row[4]),
      volume: Number(row[5]),
    });
  }
  return out;
}

interface BinanceSymbol {
  status: string;
  baseAsset: string;
  quoteAsset: string;
  isSpotTradingAllowed?: boolean;
  filters: Array<{ filterType: string; tickSize?: string; stepSize?: string }>;
}

function mapBinancePairs(info: unknown): TradingPair[] {
  const symbols = (info as { symbols?: unknown } | null)?.symbols;
  if (!Array.isArray(symbols)) throw new ProviderError('Unrecognized Binance exchangeInfo response', undefined, info);
  return (symbols as BinanceSymbol[])
    .filter((s) => s.status === 'TRADING' && s.isSpotTradingAllowed !== false)
    .map((s) => ({
      symbol: `${s.baseAsset}/${s.quoteAsset}`,
      base: s.baseAsset,
      quote: s.quoteAsset,
      exchangeId: 'binance',
      priceDecimals: decimalsOf(s.filters.find((f) => f.filterType === 'PRICE_FILTER')?.tickSize),
      quantityDecimals: decimalsOf(s.filters.find((f) => f.filterType === 'LOT_SIZE')?.stepSize),
    }))
    .sort((a, b) => a.symbol.localeCompare(b.symbol));
}

/** A 24h mini ticker as REST returns it; stream events are renamed to these fields. */
interface MiniTicker {
  symbol: string;
  openPrice: string;
  highPrice: string;
  lowPrice: string;
  lastPrice: string;
  volume: string;
  quoteVolume?: string;
  closeTime: number;
}

/** Null when the market has no last price (halted or never traded). */
function toTicker(pair: string, row: MiniTicker): Ticker24h | null {
  const last = Number(row.lastPrice);
  if (!(last > 0)) return null;
  const open = Number(row.openPrice);
  const baseVolume = Number(row.volume);
  const quoteVolume = Number(row.quoteVolume);
  return {
    exchangeId: 'binance',
    pair,
    last,
    open24h: open,
    high24h: Number(row.highPrice),
    low24h: Number(row.lowPrice),
    changePercent24h: open > 0 ? ((last - open) / open) * 100 : 0,
    baseVolume24h: baseVolume,
    quoteVolume24h: Number.isFinite(quoteVolume) ? quoteVolume : baseVolume * last,
    timestamp: row.closeTime,
  };
}

interface MiniTickerEvent {
  e?: string;
  E: number;
  s: string;
  o: string;
  h: string;
  l: string;
  c: string;
  v: string;
  q: string;
}

/** `24hrMiniTicker` stream event → Ticker24h, for the requested pairs only. */
function tickerFromEvent(event: unknown, pairBySymbol: Map<string, string>): Ticker24h | null {
  const e = event as MiniTickerEvent | null;
  const pair = e?.e === '24hrMiniTicker' ? pairBySymbol.get(e.s) : undefined;
  if (!e || !pair) return null;
  return toTicker(pair, {
    symbol: e.s,
    openPrice: e.o,
    highPrice: e.h,
    lowPrice: e.l,
    lastPrice: e.c,
    volume: e.v,
    quoteVolume: e.q,
    closeTime: e.E,
  });
}

function candleFromEvent(event: unknown): OHLCV | null {
  const e = event as { e?: string; k?: { t: number; o: string; h: string; l: string; c: string; v: string } } | null;
  const k = e?.e === 'kline' ? e.k : undefined;
  if (!k) return null;
  return {
    timestamp: k.t,
    open: Number(k.o),
    high: Number(k.h),
    low: Number(k.l),
    close: Number(k.c),
    volume: Number(k.v),
  };
}

export function binanceAdapter(options: AdapterOptions = {}): ExchangeAdapter {
  // The REST host that answered last; a geo-blocked visitor stays on the fallback for the session.
  let restBase = 0;
  let pairsCache: { at: number; pairs: Promise<TradingPair[]> } | null = null;

  async function getJson(path: string): Promise<unknown> {
    const doFetch = options.fetch ?? globalThis.fetch;
    for (let index = restBase; ; index += 1) {
      const canFallBack = index < REST_BASES.length - 1;
      let response: Response;
      try {
        response = await doFetch(`${REST_BASES[index]}${path}`, { headers: { Accept: 'application/json' } });
      } catch (error) {
        if (canFallBack) continue;
        throw new ProviderError(`Binance is unreachable: ${error instanceof Error ? error.message : String(error)}`);
      }
      if (canFallBack && (response.status === 451 || response.status === 403)) continue;
      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const message = (body as { msg?: unknown } | null)?.msg;
        throw new ProviderError(
          typeof message === 'string' ? message : `Binance request failed (${response.status})`,
          response.status,
          body,
        );
      }
      restBase = index;
      return body;
    }
  }

  async function fetchKlines(symbol: string, timeframe: Timeframe, from: number, to: number): Promise<OHLCV[]> {
    const interval = INTERVAL[timeframe];
    if (!interval) throw new ProviderError(`Unsupported timeframe ${timeframe}`);
    const pages = await mapLimit(pageWindows(from, to, timeframe, MAX_KLINES), PAGE_CONCURRENCY, ({ start, end }) => {
      const query = new URLSearchParams({
        symbol,
        interval,
        startTime: String(start),
        endTime: String(end),
        limit: String(MAX_KLINES),
      });
      return getJson(`/klines?${query}`).then(mapBinanceKlines);
    });
    return candlesInWindow(pages, from, to);
  }

  function loadPairs(): Promise<TradingPair[]> {
    if (pairsCache && Date.now() - pairsCache.at < PAIRS_TTL_MS) return pairsCache.pairs;
    const pairs = getJson('/exchangeInfo?permissions=SPOT&symbolStatus=TRADING&showPermissionSets=false').then(
      mapBinancePairs,
    );
    const entry = { at: Date.now(), pairs };
    pairsCache = entry;
    // A failed load must not be cached.
    pairs.catch(() => {
      if (pairsCache === entry) pairsCache = null;
    });
    return pairs;
  }

  /** One socket on the combined-stream endpoint; `streams` are subscribed again on every connect. */
  function openStreams(
    streams: string[],
    onData: (data: unknown) => void,
    onStatus?: (status: StreamStatus) => void,
    onConnect?: () => void,
  ): ReconnectingSocket {
    return new ReconnectingSocket({
      urls: STREAM_URLS,
      onOpen: (send) => {
        send(JSON.stringify({ method: 'SUBSCRIBE', params: streams, id: 1 }));
        onConnect?.();
      },
      onMessage: (text) => {
        // Replies to SUBSCRIBE and to the heartbeat carry `result`; a rejected request carries `error`.
        const frame = parseJson(text) as { data?: unknown; error?: unknown } | undefined;
        if (frame?.error) onStatus?.('error');
        else if (frame?.data !== undefined) onData(frame.data);
      },
      onStatus,
      heartbeat: HEARTBEAT,
      WebSocket: options.WebSocket,
    });
  }

  const adapter: ExchangeAdapter = {
    id: 'binance',
    name: 'Binance',

    getPairs: loadPairs,

    async getOHLCV({ pair, timeframe, from, to }: AdapterOHLCVRequest): Promise<OHLCV[]> {
      const symbol = toBinanceSymbol(pair);
      const source = DERIVED[timeframe];
      if (!source) return fetchKlines(symbol, timeframe, from, to);
      // Whole buckets only: from the open of the first bucket to the last millisecond of the last.
      const lastBucketEnd = bucketStart(to, timeframe) + timeframeToMs(timeframe) - 1;
      const fine = await fetchKlines(symbol, source, bucketStart(from, timeframe), lastBucketEnd);
      return aggregateToTimeframe(fine, timeframe).filter((c) => c.timestamp >= from && c.timestamp <= to);
    },

    async getTickers(): Promise<Ticker24h[]> {
      const [pairs, rows] = await Promise.all([loadPairs(), getJson('/ticker/24hr?type=MINI&symbolStatus=TRADING')]);
      if (!Array.isArray(rows)) throw new ProviderError('Unrecognized Binance ticker response', undefined, rows);
      const pairBySymbol = new Map(pairs.map((p) => [p.base + p.quote, p.symbol]));
      const tickers: Ticker24h[] = [];
      for (const row of rows as MiniTicker[]) {
        const pair = pairBySymbol.get(row.symbol);
        const ticker = pair ? toTicker(pair, row) : null;
        if (ticker) tickers.push(ticker);
      }
      return tickers;
    },

    subscribeOHLCV(pair, timeframe, onCandle, onStatus): Unsubscribe {
      const source = DERIVED[timeframe];
      const rollUp = source
        ? rollUpStream(
            timeframe,
            (from, to) => adapter.getOHLCV({ pair, timeframe: source, from, to }),
            onCandle,
            onStatus,
          )
        : null;
      const socket = openStreams(
        [`${toBinanceSymbol(pair).toLowerCase()}@kline_${INTERVAL[source ?? timeframe]}`],
        (data) => {
          const candle = candleFromEvent(data);
          if (!candle) return;
          if (rollUp) rollUp.push(candle);
          else onCandle(candle);
        },
        onStatus,
        () => rollUp?.seed(),
      );
      return () => {
        rollUp?.stop();
        socket.close();
      };
    },

    subscribeTickers(pairs, onTicker): Unsubscribe {
      const pairBySymbol = new Map(pairs.map((pair) => [toBinanceSymbol(pair), pair]));
      if (pairBySymbol.size === 0) return () => {};
      // Past the per-connection stream cap only the all-market stream fits on one socket.
      const streams =
        pairBySymbol.size > MAX_STREAMS
          ? ['!miniTicker@arr']
          : [...pairBySymbol.keys()].map((symbol) => `${symbol.toLowerCase()}@miniTicker`);
      const socket = openStreams(streams, (data) => {
        for (const event of Array.isArray(data) ? data : [data]) {
          const ticker = tickerFromEvent(event, pairBySymbol);
          if (ticker) onTicker(ticker);
        }
      });
      return () => socket.close();
    },
  };

  return adapter;
}

// Venue-neutral helpers below are shared with the Bybit and OKX adapters.

/** Page requests one `getOHLCV` keeps in flight at most. */
export const PAGE_CONCURRENCY = 6;

const DAY_MS = 86_400_000;
const SEED_RETRY_MS = 10_000;

/** Decimals of a tick or step size: "0.01000000" → 2, "0.5" → 1, "1.00" → 0. */
export function decimalsOf(step: string | undefined): number {
  if (!step || !(Number(step) > 0)) return 8;
  const fraction = step.trim().split('.')[1] ?? '';
  return fraction.replace(/0+$/, '').length;
}

/**
 * Split [from, to] into consecutive windows that each hold at most `perPage`
 * candles of `timeframe` (a month counts as 28 days, so no window overflows).
 */
export function pageWindows(
  from: number,
  to: number,
  timeframe: Timeframe,
  perPage: number,
): Array<{ start: number; end: number }> {
  const span = (timeframe === '1M' ? 28 * DAY_MS : timeframeToMs(timeframe)) * perPage;
  const windows: Array<{ start: number; end: number }> = [];
  for (let start = Math.max(0, from); start <= to; start += span) {
    windows.push({ start, end: Math.min(to, start + span - 1) });
  }
  return windows;
}

/** Run `task` over `items` with at most `limit` in flight; results keep the input order. */
export async function mapLimit<T, R>(items: T[], limit: number, task: (item: T) => Promise<R>): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  let failed = false;
  async function worker(): Promise<void> {
    // After a failure the call has already rejected; stop asking for pages nobody will read.
    while (!failed && next < items.length) {
      const index = next;
      next += 1;
      try {
        results[index] = await task(items[index]);
      } catch (error) {
        failed = true;
        throw error;
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

/** Pages merged into one oldest-first series: one candle per timestamp, from <= timestamp <= to. */
export function candlesInWindow(pages: OHLCV[][], from: number, to: number): OHLCV[] {
  return mergeCandles([], pages.flat()).filter((candle) => candle.timestamp >= from && candle.timestamp <= to);
}

/** `JSON.parse` that yields undefined for frames that are not JSON (a plain `pong`, say). */
export function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export interface RollUpStream {
  /** Call on every (re)connect: reloads the current bucket's finer candles from REST. */
  seed(): void;
  /** Call with every streamed finer candle. */
  push(candle: OHLCV): void;
  stop(): void;
}

/**
 * Live candles for a timeframe the venue does not stream, rolled up from a
 * finer native stream. The current bucket's finer candles come from one REST
 * load per connection plus the stream; nothing is emitted until that load
 * landed, so the chart never shows a bucket without its earlier part. A
 * failed load is reported as 'error' and retried on a later frame.
 */
export function rollUpStream(
  timeframe: Timeframe,
  load: (from: number, to: number) => Promise<OHLCV[]>,
  onCandle: (candle: OHLCV) => void,
  onStatus?: (status: StreamStatus) => void,
): RollUpStream {
  const parts = new Map<number, OHLCV>();
  let bucket = -Infinity;
  let ready = false;
  let loading = false;
  let failed = false;
  let stopped = false;
  let attempt = 0;
  let lastAttemptAt = 0;

  function absorb(candles: OHLCV[]): void {
    for (const candle of candles) {
      const start = bucketStart(candle.timestamp, timeframe);
      if (start < bucket) continue;
      if (start > bucket) {
        bucket = start;
        parts.clear();
      }
      // Volume only grows while a candle forms, so the larger copy (REST or stream) is the fresher one.
      const known = parts.get(candle.timestamp);
      if (!known || candle.volume >= known.volume) parts.set(candle.timestamp, candle);
    }
  }

  function emit(): void {
    const [current] = aggregateToTimeframe([...parts.values()], timeframe);
    if (current) onCandle(current);
  }

  function seed(): void {
    const id = ++attempt;
    ready = false;
    loading = true;
    lastAttemptAt = Date.now();
    load(bucketStart(lastAttemptAt, timeframe), lastAttemptAt).then(
      (candles) => {
        if (stopped || id !== attempt) return;
        loading = false;
        absorb(candles);
        ready = true;
        if (failed) {
          failed = false;
          onStatus?.('open');
        }
        emit();
      },
      () => {
        if (stopped || id !== attempt) return;
        loading = false;
        failed = true;
        onStatus?.('error');
      },
    );
  }

  return {
    seed,
    push(candle) {
      absorb([candle]);
      if (ready) emit();
      else if (!loading && Date.now() - lastAttemptAt >= SEED_RETRY_MS) seed();
    },
    stop() {
      stopped = true;
    },
  };
}
